import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Search, Eye, Trash2, User, Calendar, AlertCircle,
  Plus, Edit, Filter, X, CheckCircle, RefreshCw, Send,
  Clock, UserPlus, Shield, Tag, Key, MoreVertical
} from 'lucide-react';
import toast from 'react-hot-toast';
import invitationService from '../services/invitationService';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import tagService from '../services/tagService';
import EnhancedDataTable from './EnhancedDataTableV2';
import ResponsiveModal, { ResponsiveButton } from './ResponsiveModal';
import { PageLoader } from './CommonLoader';
import { CreateButton } from './CommonButtons';
import SearchableSelect from './SearchableSelect';
import MultiSelectSearchable from './MultiSelectSearchable';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail, validateRequired, ErrorMessage } from '../utils/validation';

const DataroomInvitations = ({ dataroomUuid }) => {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState({});

  // Form states
  const [selectionType, setSelectionType] = useState('permission');
  const [formData, setFormData] = useState({
    email: '',
    dataroom_role_id: '',
    permission_ids: [],
    tag_id: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // Determine if user is super admin
  const isSuperAdmin = user?.role === 'super-admin' || user?.roles?.includes('super-admin');

  useEffect(() => {
    fetchInvitations();
  }, [pagination.current_page, searchTerm, selectedStatus, dataroomUuid]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowActionsMenu({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchRolesAndPermissions = async () => {
    try {
      console.log('🔄 Fetching roles and permissions...');

      // Fetch roles based on user type
      let rolesData = [];
      try {
        const rolesResponse = isSuperAdmin
          ? await roleService.superAdminDataroom.list(dataroomUuid)
          : await roleService.dataroom.list(dataroomUuid);

        console.log('Raw roles response:', rolesResponse);

        // Extract roles - check all possible response structures
        if (rolesResponse?.roles && Array.isArray(rolesResponse.roles)) {
          rolesData = rolesResponse.roles;
        } else if (rolesResponse?.data?.roles && Array.isArray(rolesResponse.data.roles)) {
          rolesData = rolesResponse.data.roles;
        } else if (Array.isArray(rolesResponse?.data)) {
          rolesData = rolesResponse.data;
        } else if (Array.isArray(rolesResponse)) {
          rolesData = rolesResponse;
        }

        console.log('✅ Extracted roles:', rolesData.length, rolesData);
      } catch (error) {
        console.error('Error fetching roles:', error);
      }

      // Fetch permissions
      let permissionsData = [];
      try {
        const permResponse = await permissionService.list();
        console.log('Raw permissions response:', permResponse);

        // Extract permissions
        if (permResponse?.permissions && Array.isArray(permResponse.permissions)) {
          permissionsData = permResponse.permissions;
        } else if (permResponse?.data?.permissions && Array.isArray(permResponse.data.permissions)) {
          permissionsData = permResponse.data.permissions;
        } else if (Array.isArray(permResponse?.data)) {
          permissionsData = permResponse.data;
        } else if (Array.isArray(permResponse)) {
          permissionsData = permResponse;
        }

        console.log('✅ Extracted permissions:', permissionsData.length, permissionsData);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }

      // Fetch tags
      let tagsData = [];
      try {
        const tagsResponse = isSuperAdmin
          ? await tagService.getSuperAdminDataroomTags(dataroomUuid)
          : await tagService.getTags(dataroomUuid);

        console.log('Raw tags response:', tagsResponse);

        // Extract tags from response
        if (tagsResponse?.tags && Array.isArray(tagsResponse.tags)) {
          tagsData = tagsResponse.tags;
        } else if (tagsResponse?.data?.tags && Array.isArray(tagsResponse.data.tags)) {
          tagsData = tagsResponse.data.tags;
        } else if (Array.isArray(tagsResponse?.data)) {
          tagsData = tagsResponse.data;
        } else if (Array.isArray(tagsResponse)) {
          tagsData = tagsResponse;
        }

        console.log('✅ Extracted tags:', tagsData.length, tagsData);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }

      // Update state
      setRoles(rolesData);
      setPermissions(permissionsData);
      setTags(tagsData);

      console.log('✅ State updated - Roles:', rolesData.length, 'Permissions:', permissionsData.length);

      return { rolesData, permissionsData, tagsData };
    } catch (error) {
      console.error('Error in fetchRolesAndPermissions:', error);
      setRoles([]);
      setPermissions([]);
      setTags([]);
      return { rolesData: [], permissionsData: [], tagsData: [] };
    }
  };

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      };

      let response;
      if (isSuperAdmin) {
        // Use super admin endpoint
        response = await invitationService.admin.getByDataroom(dataroomUuid, params);
      } else {
        // Use regular admin endpoint
        response = await invitationService.list(dataroomUuid, params);
      }

      if (response && response.invitations && Array.isArray(response.invitations)) {
        setInvitations(response.invitations);

        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        setInvitations([]);
      }
    } catch (error) {
      toast.error('Failed to load invitations');
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      errors.email = emailError;
    }

    // Validate role or permissions based on selection type
    if (selectionType === 'role') {
      const roleError = validateRequired(formData.dataroom_role_id, 'Role');
      if (roleError) {
        errors.dataroom_role_id = roleError;
      }
    } else {
      if (!formData.permission_ids || formData.permission_ids.length === 0) {
        errors.permission_ids = 'At least one permission is required';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        email: formData.email
      };

      // Add tag if selected
      if (formData.tag_id) {
        dataToSubmit.tag_id = formData.tag_id;
      }

      // Include either role or permissions based on selection
      if (selectionType === 'role') {
        dataToSubmit.dataroom_role_id = formData.dataroom_role_id;
      } else {
        dataToSubmit.permission_ids = formData.permission_ids;
      }

      if (isSuperAdmin) {
        await invitationService.admin.createInvitation(dataroomUuid, dataToSubmit);
      } else {
        await invitationService.create(dataroomUuid, dataToSubmit);
      }

      toast.success('Invitation created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchInvitations();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create invitation';
      toast.error(errorMessage);
      console.error('Error creating invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateInvitation = async (e) => {
    e.preventDefault();

    if (!selectedInvitation) {
      toast.error('No invitation selected');
      return;
    }

    if (!selectedInvitation.id) {
      toast.error('Invalid invitation ID');
      console.error('selectedInvitation:', selectedInvitation);
      return;
    }

    if (!validateForm()) return;

    console.log('📤 Updating invitation ID:', selectedInvitation.id);

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        email: formData.email
      };

      // Add tag if selected
      if (formData.tag_id) {
        dataToSubmit.tag_id = formData.tag_id;
      }

      // Include either role or permissions based on selection
      if (selectionType === 'role') {
        dataToSubmit.dataroom_role_id = formData.dataroom_role_id;
      } else {
        dataToSubmit.permission_ids = formData.permission_ids;
      }

      console.log('📤 Update data:', dataToSubmit);

      if (isSuperAdmin) {
        await invitationService.admin.updateInvitation(
          dataroomUuid,
          selectedInvitation.id,
          dataToSubmit
        );
      } else {
        await invitationService.update(
          dataroomUuid,
          selectedInvitation.id,
          dataToSubmit
        );
      }

      toast.success('Invitation updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchInvitations();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update invitation';
      toast.error(errorMessage);
      console.error('Error updating invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInvitation = async () => {
    if (!selectedInvitation) return;

    setIsSubmitting(true);
    try {
      if (isSuperAdmin) {
        await invitationService.admin.deleteInvitation(
          dataroomUuid,
          selectedInvitation.id
        );
      } else {
        await invitationService.delete(
          dataroomUuid,
          selectedInvitation.id
        );
      }

      toast.success('Invitation deleted successfully');
      setShowDeleteModal(false);
      setSelectedInvitation(null);
      fetchInvitations();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete invitation';
      toast.error(errorMessage);
      console.error('Error deleting invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      if (isSuperAdmin) {
        await invitationService.admin.resendInvitation(dataroomUuid, invitation.id);
      } else {
        await invitationService.resend(dataroomUuid, invitation.id);
      }

      toast.success('Invitation resent successfully');
      fetchInvitations();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend invitation';
      toast.error(errorMessage);
      console.error('Error resending invitation:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      dataroom_role_id: '',
      permission_ids: [],
      tag_id: ''
    });
    setFormErrors({});
    setSelectionType('permission');
    setSelectedInvitation(null);
  };

  const openEditModal = async (invitation) => {
    try {
      console.log('🔄 Opening edit modal for invitation:', invitation.id);

      // Fetch fresh roles, permissions, tags
      await fetchRolesAndPermissions();

      // Fetch invitation details from API
      const invitationData = isSuperAdmin
        ? await invitationService.admin.getInvitation(dataroomUuid, invitation.id)
        : await invitationService.get(dataroomUuid, invitation.id);

      console.log('Raw invitation data from API:', invitationData);
      console.log('invitationData.data:', invitationData?.data);
      console.log('invitationData.invitation:', invitationData?.invitation);

      // Extract invitation from response - try all possible structures
      let invitationDetails;

      if (invitationData?.data?.invitation) {
        invitationDetails = invitationData.data.invitation;
      } else if (invitationData?.invitation) {
        invitationDetails = invitationData.invitation;
      } else if (invitationData?.data) {
        invitationDetails = invitationData.data;
      } else {
        invitationDetails = invitationData || invitation;
      }

      console.log('Extracted invitation details:', invitationDetails);
      console.log('Email from details:', invitationDetails?.email);

      // Ensure we have the ID - use the original invitation ID if not present in response
      if (!invitationDetails.id) {
        invitationDetails.id = invitation.id;
      }

      // Ensure we have the email - fallback to original invitation
      if (!invitationDetails.email && invitation.email) {
        console.warn('Email not in API response, using original invitation email');
        invitationDetails.email = invitation.email;
      }

      // Fallback for other critical fields
      if (!invitationDetails.dataroom_role_id && invitation.dataroom_role_id) {
        invitationDetails.dataroom_role_id = invitation.dataroom_role_id;
      }

      if (!invitationDetails.role && invitation.role) {
        invitationDetails.role = invitation.role;
      }

      console.log('Setting selectedInvitation with ID:', invitationDetails.id);
      console.log('Final invitation details with fallbacks:', invitationDetails);
      setSelectedInvitation(invitationDetails);

      // Populate form with fetched data
      // Get role ID and convert type to match available roles
      let roleId = invitationDetails.dataroom_role_id || invitationDetails.role?.id || '';

      // Convert roleId type to match the first role's ID type
      if (roleId && roles.length > 0) {
        const firstRoleIdType = typeof roles[0].id;
        if (firstRoleIdType === 'number' && typeof roleId === 'string') {
          roleId = parseInt(roleId, 10);
        } else if (firstRoleIdType === 'string' && typeof roleId === 'number') {
          roleId = String(roleId);
        }
      }

      // Get permission IDs and convert types
      let permissionIds = [];

      // Check if permission_ids is directly available (array of IDs)
      if (invitationDetails.permission_ids && Array.isArray(invitationDetails.permission_ids)) {
        permissionIds = invitationDetails.permission_ids.map(id => {
          // Match type with available permissions
          if (permissions.length > 0) {
            const firstPermIdType = typeof permissions[0].id;
            if (firstPermIdType === 'number' && typeof id === 'string') {
              return parseInt(id, 10);
            } else if (firstPermIdType === 'string' && typeof id === 'number') {
              return String(id);
            }
          }
          return id;
        });
      }
      // Check if permissions is array of IDs (e.g., [7, 3])
      else if (invitationDetails.permissions && Array.isArray(invitationDetails.permissions)) {
        // Check if first element is a primitive (number/string) or object
        const firstPerm = invitationDetails.permissions[0];
        if (typeof firstPerm === 'number' || typeof firstPerm === 'string') {
          // It's an array of IDs
          permissionIds = invitationDetails.permissions.map(id => {
            // Match type with available permissions
            if (permissions.length > 0) {
              const firstPermIdType = typeof permissions[0].id;
              if (firstPermIdType === 'number' && typeof id === 'string') {
                return parseInt(id, 10);
              } else if (firstPermIdType === 'string' && typeof id === 'number') {
                return String(id);
              }
            }
            return id;
          });
        } else if (firstPerm && typeof firstPerm === 'object') {
          // It's an array of objects with id property
          permissionIds = invitationDetails.permissions.map(p => {
            let id = p.id;
            // Match type with available permissions
            if (permissions.length > 0) {
              const firstPermIdType = typeof permissions[0].id;
              if (firstPermIdType === 'number' && typeof id === 'string') {
                id = parseInt(id, 10);
              } else if (firstPermIdType === 'string' && typeof id === 'number') {
                id = String(id);
              }
            }
            return id;
          });
        }
      }

      // Get tag ID and convert type
      let tagId = '';

      // Check if tag_id is directly available
      if (invitationDetails.tag_id) {
        tagId = invitationDetails.tag_id;
      }
      // Fallback to tags array
      else if (invitationDetails.tags && invitationDetails.tags.length > 0) {
        tagId = invitationDetails.tags[0].id;
      }

      // Convert type to match available tags
      if (tagId && tags.length > 0) {
        const firstTagIdType = typeof tags[0].id;
        if (firstTagIdType === 'number' && typeof tagId === 'string') {
          tagId = parseInt(tagId, 10);
        } else if (firstTagIdType === 'string' && typeof tagId === 'number') {
          tagId = String(tagId);
        }
      }

      const formDataToSet = {
        email: invitationDetails.email || '',
        dataroom_role_id: roleId,
        permission_ids: permissionIds,
        tag_id: tagId
      };

      console.log('📝 Form data to set (after type conversion):', formDataToSet);
      console.log('📋 Role ID:', roleId, 'Type:', typeof roleId);
      console.log('📋 Available roles:', roles.map(r => ({ id: r.id, type: typeof r.id, name: r.name })));
      console.log('🔑 Permission IDs:', permissionIds);
      console.log('📋 Available permissions:', permissions.map(p => ({ id: p.id, type: typeof p.id, name: p.name })));
      console.log('🏷️ Tag ID:', tagId, 'Type:', typeof tagId);
      console.log('📋 Available tags:', tags.map(t => ({ id: t.id, type: typeof t.id, name: t.name })));

      setFormData(formDataToSet);

      // Determine selection type based on existing data
      if (invitationDetails.dataroom_role_id || invitationDetails.role?.id) {
        console.log('Setting selection type to: role');
        setSelectionType('role');
      } else if (
        (invitationDetails.permission_ids && invitationDetails.permission_ids.length > 0) ||
        (invitationDetails.permissions && invitationDetails.permissions.length > 0)
      ) {
        console.log('Setting selection type to: permission');
        setSelectionType('permission');
      } else {
        console.log('Setting selection type to: permission (default)');
        setSelectionType('permission'); // default
      }

      console.log('✅ Form populated, opening edit modal');
      setShowEditModal(true);
    } catch (error) {
      console.error('Error opening edit modal:', error);
      toast.error('Failed to load invitation details');
    }
  };

  const openViewModal = (invitation) => {
    setSelectedInvitation(invitation);
    setShowViewModal(true);
  };

  const openDeleteModal = (invitation) => {
    setSelectedInvitation(invitation);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (status, statusText) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      declined: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      0: 'bg-yellow-100 text-yellow-800', // pending
      1: 'bg-green-100 text-green-800', // accepted
      2: 'bg-yellow-100 text-yellow-800', // pending
      3: 'bg-red-100 text-red-800', // expired
      4: 'bg-gray-100 text-gray-800' // declined
    };

    // Use status_text if available, otherwise fall back to status
    const displayText = statusText || status || 'unknown';

    // Convert status to string and lowercase for style key lookup
    let styleKey = String(status).toLowerCase();

    // Map numeric status to string equivalents for styling
    if (status === 0 || status === '0' || status === 2 || status === '2') {
      styleKey = 'pending';
    } else if (status === 1 || status === '1') {
      styleKey = 'accepted';
    } else if (status === 3 || status === '3') {
      styleKey = 'expired';
    } else if (status === 4 || status === '4') {
      styleKey = 'declined';
    }

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[styleKey] || 'bg-gray-100 text-gray-800'}`}>
        {displayText}
      </span>
    );
  };

  const columns = [
    {
      key: 'email',
      label: 'Email',
      render: (value, row) => (
        <div className="flex items-center">
          <Mail className="w-4 h-4 mr-2 text-gray-400" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      key: 'role',
      label: 'Role/Permissions',
      render: (value, row) => {
        if (row.role) {
          return (
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-blue-500" />
              <span>{row.role.name}</span>
            </div>
          );
        } else if (row.permissions && row.permissions.length > 0) {
          return (
            <div className="flex items-center">
              <Key className="w-4 h-4 mr-2 text-purple-500" />
              <span>{row.permissions.length} permissions</span>
            </div>
          );
        }
        return '-';
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => getStatusBadge(row.status, row.status_text)
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    }
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with filters */}
        <div className="border-b px-6 py-4">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-6">
          <div className="flex flex-1 gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invitations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="expired">Expired</option>
              <option value="declined">Declined</option>
            </select>
          </div>

          <div className="flex gap-3">
            <CreateButton
            onClick={async () => {
              console.log('🚀 Create Invitation clicked');
              // Fetch fresh data
              const { rolesData, permissionsData } = await fetchRolesAndPermissions();
              console.log('📦 Data fetched - Opening modal with:', rolesData.length, 'roles and', permissionsData.length, 'permissions');
              // Open modal
              setShowCreateModal(true);
            }}
            label="Create Invitation"
          />
          </div>
        </div>
        </div>

      {/* Content */}
      <div className="px-6 py-4">
        <EnhancedDataTable
        columns={columns}
        data={invitations}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
        loading={loading}
        emptyMessage="No invitations found"
        actions={(row) => {
          if (!row || !row.id) {
            return null;
          }

          const isPending = row.status === 'pending' || row.status === 0 || row.status === '0' || row.status === 2 || row.status === '2';

          return (
            <div className="flex items-center justify-end">
              <div className="relative">
                <button
                  type="button"
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    console.log('Action button clicked for row:', row.id);
                    console.log('Current menu state:', showActionsMenu);

                    const rect = e.currentTarget.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceAbove = rect.top;
                    const dropdownHeight = 200;

                    const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;

                    const newMenuState = {
                      [row.id]: !showActionsMenu[row.id],
                      [`${row.id}_position`]: shouldOpenUpward ? 'top' : 'bottom',
                      [`${row.id}_rect`]: rect
                    };

                    console.log('Setting new menu state:', newMenuState);
                    setShowActionsMenu(newMenuState);
                  }}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {showActionsMenu[row.id] && (
                  <div
                    className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      zIndex: 9999,
                      right: `${window.innerWidth - showActionsMenu[`${row.id}_rect`]?.right}px`,
                      ...(showActionsMenu[`${row.id}_position`] === 'top' ? {
                        bottom: `${window.innerHeight - showActionsMenu[`${row.id}_rect`]?.top + 8}px`
                      } : {
                        top: `${showActionsMenu[`${row.id}_rect`]?.bottom + 8}px`
                      })
                    }}
                  >
                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openViewModal(row);
                          setShowActionsMenu({});
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResendInvitation(row);
                          setShowActionsMenu({});
                        }}
                        disabled={!isPending}
                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                          isPending
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Send className="w-4 h-4" />
                        Resend
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(row);
                          setShowActionsMenu({});
                        }}
                        disabled={!isPending}
                        className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${
                          isPending
                            ? 'text-gray-700 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>

                      <div className="border-t border-gray-200 my-1"></div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(row);
                          setShowActionsMenu({});
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        }}
        />
      </div>
    </div>

      {/* Create Modal */}
      <ResponsiveModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create Invitation"
        size="large"
      >
        <form onSubmit={handleCreateInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, email: e.target.value.toLowerCase() }));
                // Clear error on change
                if (formErrors.email) {
                  setFormErrors({ ...formErrors, email: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 ${
                formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Enter email address"
            />
            {formErrors.email && <ErrorMessage error={formErrors.email} />}
          </div>

          {/* Access Type Selection */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Access Type *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'permission'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                  }
                `}
              >
                <input
                  type="radio"
                  value="permission"
                  checked={selectionType === 'permission'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="sr-only"
                />
                {selectionType === 'permission' && (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                )}
                <div className="flex items-center flex-1">
                  <div className={`p-1.5 rounded mr-2 ${selectionType === 'permission' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <Key className={`w-4 h-4 ${selectionType === 'permission' ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <span className={`font-medium text-xs ${selectionType === 'permission' ? 'text-primary-900' : 'text-gray-700'}`}>
                    Custom Permissions
                  </span>
                </div>
              </label>

              <label
                className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'role'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                  }
                `}
              >
                <input
                  type="radio"
                  value="role"
                  checked={selectionType === 'role'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="sr-only"
                />
                {selectionType === 'role' && (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                )}
                <div className="flex items-center flex-1">
                  <div className={`p-1.5 rounded mr-2 ${selectionType === 'role' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <Shield className={`w-4 h-4 ${selectionType === 'role' ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <span className={`font-medium text-xs ${selectionType === 'role' ? 'text-primary-900' : 'text-gray-700'}`}>
                    Predefined Role
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Role/Permission Selection */}
          {selectionType === 'role' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Select Role *
              </label>
              <SearchableSelect
                value={formData.dataroom_role_id}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, dataroom_role_id: e.target.value }));
                  // Clear error on change
                  if (formErrors.dataroom_role_id) {
                    setFormErrors({ ...formErrors, dataroom_role_id: '' });
                  }
                }}
                options={roles}
                optionLabel="name"
                optionValue="id"
                placeholder="Choose a predefined role"
                searchPlaceholder="Search roles..."
                error={formErrors.dataroom_role_id}
              />
              {formErrors.dataroom_role_id && <ErrorMessage error={formErrors.dataroom_role_id} />}
              <p className="mt-1.5 text-xs text-gray-500">
                Select a predefined role with preset permissions
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Select Permissions *
              </label>
              <MultiSelectSearchable
                value={formData.permission_ids}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, permission_ids: e.target.value }));
                  // Clear error on change
                  if (formErrors.permission_ids) {
                    setFormErrors({ ...formErrors, permission_ids: '' });
                  }
                }}
                options={permissions}
                optionLabel="name"
                optionValue="id"
                placeholder="Choose one or more permissions"
                searchPlaceholder="Search permissions..."
                error={formErrors.permission_ids}
              />
              {formErrors.permission_ids && <ErrorMessage error={formErrors.permission_ids} />}
              <p className="mt-1.5 text-xs text-gray-500">
                Select specific permissions for granular access control
              </p>
            </div>
          )}

          {/* Tag Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Assign Tag <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <SearchableSelect
              value={formData.tag_id}
              onChange={(e) => setFormData(prev => ({ ...prev, tag_id: e.target.value }))}
              options={tags}
              optionLabel="name"
              optionValue="id"
              placeholder="Select a tag to categorize this invitation"
              searchPlaceholder="Search tags..."
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Tags help organize and filter invitations
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              type="button"
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Invitation'}
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* Edit Modal */}
      <ResponsiveModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title="Edit Invitation"
        size="large"
      >
        <form onSubmit={handleUpdateInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, email: e.target.value.toLowerCase() }));
                // Clear error on change
                if (formErrors.email) {
                  setFormErrors({ ...formErrors, email: '' });
                }
              }}
              className={`w-full px-3 py-1.5 border rounded-md focus:outline-none focus:ring-2 ${
                formErrors.email ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Enter email address"
            />
            {formErrors.email && <ErrorMessage error={formErrors.email} />}
          </div>

          {/* Access Type Selection */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Access Type *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'permission'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                  }
                `}
              >
                <input
                  type="radio"
                  value="permission"
                  checked={selectionType === 'permission'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="sr-only"
                />
                {selectionType === 'permission' && (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                )}
                <div className="flex items-center flex-1">
                  <div className={`p-1.5 rounded mr-2 ${selectionType === 'permission' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <Key className={`w-4 h-4 ${selectionType === 'permission' ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <span className={`font-medium text-xs ${selectionType === 'permission' ? 'text-primary-900' : 'text-gray-700'}`}>
                    Custom Permissions
                  </span>
                </div>
              </label>

              <label
                className={`
                  relative flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all
                  ${selectionType === 'role'
                    ? 'border-primary-500 bg-primary-50 shadow-sm'
                    : 'border-gray-300 bg-white hover:border-gray-400'
                  }
                `}
              >
                <input
                  type="radio"
                  value="role"
                  checked={selectionType === 'role'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="sr-only"
                />
                {selectionType === 'role' && (
                  <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary-600" />
                )}
                <div className="flex items-center flex-1">
                  <div className={`p-1.5 rounded mr-2 ${selectionType === 'role' ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    <Shield className={`w-4 h-4 ${selectionType === 'role' ? 'text-primary-600' : 'text-gray-500'}`} />
                  </div>
                  <span className={`font-medium text-xs ${selectionType === 'role' ? 'text-primary-900' : 'text-gray-700'}`}>
                    Predefined Role
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Role/Permission Selection */}
          {selectionType === 'role' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Select Role *
              </label>
              <SearchableSelect
                value={formData.dataroom_role_id}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, dataroom_role_id: e.target.value }));
                  // Clear error on change
                  if (formErrors.dataroom_role_id) {
                    setFormErrors({ ...formErrors, dataroom_role_id: '' });
                  }
                }}
                options={roles}
                optionLabel="name"
                optionValue="id"
                placeholder="Choose a predefined role"
                searchPlaceholder="Search roles..."
                error={formErrors.dataroom_role_id}
              />
              {formErrors.dataroom_role_id && <ErrorMessage error={formErrors.dataroom_role_id} />}
              <p className="mt-1.5 text-xs text-gray-500">
                Select a predefined role with preset permissions
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Select Permissions *
              </label>
              <MultiSelectSearchable
                value={formData.permission_ids}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, permission_ids: e.target.value }));
                  // Clear error on change
                  if (formErrors.permission_ids) {
                    setFormErrors({ ...formErrors, permission_ids: '' });
                  }
                }}
                options={permissions}
                optionLabel="name"
                optionValue="id"
                placeholder="Choose one or more permissions"
                searchPlaceholder="Search permissions..."
                error={formErrors.permission_ids}
              />
              {formErrors.permission_ids && <ErrorMessage error={formErrors.permission_ids} />}
              <p className="mt-1.5 text-xs text-gray-500">
                Select specific permissions for granular access control
              </p>
            </div>
          )}

          {/* Tag Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Assign Tag <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <SearchableSelect
              value={formData.tag_id}
              onChange={(e) => setFormData(prev => ({ ...prev, tag_id: e.target.value }))}
              options={tags}
              optionLabel="name"
              optionValue="id"
              placeholder="Select a tag to categorize this invitation"
              searchPlaceholder="Search tags..."
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Tags help organize and filter invitations
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              type="button"
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Invitation'}
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* View Modal */}
      <ResponsiveModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvitation(null);
        }}
        title="Invitation Details"
      >
        {selectedInvitation && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{selectedInvitation.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                {getStatusBadge(selectedInvitation.status, selectedInvitation.status_text)}
              </div>
            </div>

            {selectedInvitation.role && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Role</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvitation.role.name}</p>
              </div>
            )}

            {selectedInvitation.permissions && selectedInvitation.permissions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Permissions</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedInvitation.permissions.map(perm => (
                    <span key={perm.id} className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                      {perm.display_name || perm.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedInvitation.tags && selectedInvitation.tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Tags</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedInvitation.tags.map(tag => (
                    <span key={tag.id} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedInvitation.message && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Message</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvitation.message}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500">Created At</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(selectedInvitation.created_at).toLocaleString()}
              </p>
            </div>

            {selectedInvitation.expires_at && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Expires At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedInvitation.expires_at).toLocaleString()}
                </p>
              </div>
            )}

            {selectedInvitation.accepted_at && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Accepted At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedInvitation.accepted_at).toLocaleString()}
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <ResponsiveButton
                variant="secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedInvitation(null);
                }}
              >
                Close
              </ResponsiveButton>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedInvitation(null);
        }}
        title="Delete Invitation"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>Are you sure you want to delete this invitation?</p>
          </div>

          {selectedInvitation && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-600">
                Email: <span className="font-medium text-gray-900">{selectedInvitation.email}</span>
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedInvitation(null);
              }}
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="danger"
              onClick={handleDeleteInvitation}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default DataroomInvitations;